import React, {
	type SyntheticEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import isEmpty from 'lodash/isEmpty';
import uuid from 'uuid';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Provider as SmartCardProvider } from '@atlaskit/smart-card';
// eslint-disable-next-line jira/wrm/no-load-bridge
import { loadBridge } from '@atlassian/jira-common-bridge/src';
import { ROUTE_NAMES } from '@atlassian/jira-common-constants/src/spa-routes';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ff } from '@atlassian/jira-feature-flagging';
import { toFlagId, useFlagsService } from '@atlassian/jira-flags';
import type { FlagCustomRenderProps } from '@atlassian/jira-flags/src/services/types';
import { useInlineFieldsActions } from '@atlassian/jira-forge-controllers-inline-fields-state';
import { useUIModificationsConsentStatus } from '@atlassian/jira-issue-adjustments';
import { UIM_CONSENT_REQUIRED } from '@atlassian/jira-issue-adjustments/src/types';
import FindFieldModal from '@atlassian/jira-issue-config-find-field';
import { successFlagDisplay } from '@atlassian/jira-issue-create-analytics';
import {
	type DirtyFields,
	type FormState,
	ISSUE_CREATE_TYPE,
	REQUEST_TYPE,
	type FormFieldValues,
	type ClassificationFieldProps,
} from '@atlassian/jira-issue-create-common-types';
import { transformProjectType } from '@atlassian/jira-issue-create-commons';
import { useClassificationFieldProps } from '@atlassian/jira-issue-create-commons/src/ui/data-classification-field/utils';
import ConfigureFieldsModal from '@atlassian/jira-issue-create-configure-fields';
import {
	GIC_LIFECYCLE_EVENTS,
	useGICLifeCycleEvents,
} from '@atlassian/jira-issue-create-extensibility';
import { useFormContext } from '@atlassian/jira-issue-create-form-context';
import type { StatusTransition } from '@atlassian/jira-issue-field-status';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal';
import { isPartOfDiscoverabilityTriggers } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/utils';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils';
import {
	ContextualAnalyticsData,
	fireScreenAnalyticsDeferred,
	fireUIAnalytics,
	MODAL,
	MountEvent,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import { usePersistentIssueCreateModalViewResource } from '@atlassian/jira-router-resources-persistent-issue-create-modal-view';
import { useStorageEnforcement } from '@atlassian/jira-software-enforce-storage-limits';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import { UiModificationsContainer } from '@atlassian/jira-ui-modifications-view-gic';
import { UiModificationsEntryPoint } from '@atlassian/jira-ui-modifications-view-gic/src/ui/entry-point';
import type { CreatedIssue, CreatedIssues } from '../../common/types';
import { scrollToField } from '../../common/utils';
import { getIssueDiscoveryUrl } from '../../common/utils/issue-discovery';
import { useSmartFieldsMachineLearning } from '../../common/utils/use-smart-fields-ml';
import { useChangerSpotlight } from '../../controllers/changer-spotlight';
import { useIsGICPositioningEnabled } from '../../controllers/gic-positioning-enabled';
import { useRequestTypeFieldsLayoutPreference } from '../../controllers/request-type-fields-layout-user-preference';
import { useIssueCreateFromAnywhereContext } from '../../controllers/use-issue-create-from-anywhere-context';
import { useSitePickerContext } from '../../controllers/use-site-picker-context';
import type { IssueCreateResponse } from '../../services/create-issue/types';
import { useFetchForgeModules } from '../../services/fetch-forge-modules';
import useFetchTransitionsService from '../../services/fetch-transitions';
import useModalLoadQuery from '../../services/modal-load-query';
import {
	DiscardSuggestionsModal,
	type ConfirmDismissAiSuggestedModal,
	DISCARD_MODAL_TYPE,
	useShouldShowConfirmDismissAiSuggestedModal,
	useConfirmDismissAiSuggestedModal,
} from './confirm-dismiss-ai-suggested-modal';
import ConfirmDismissModal from './confirm-dismiss-modal';
import CreateForm, { getIssueCreateProjectForMount } from './create-form';
import type { CreateFormProps } from './create-form/types';
import { getQueryVariables, shouldShowStatusField } from './create-form/utils';
import ErrorState from './error-state';
import { ExistingGICWarning } from './existing-gic-warning';
import { JsmRequestTypeFieldsChangeboarding } from './jsm-request-type-fields-changeboarding';
import { ModalWrapper } from './modal-wrapper';
import { NavigateWithoutRestorePrompt } from './navigate-without-restore-prompt';
import SuccessFlag from './success-flag';
import type { SuccessFlagParams } from './success-flag/types';
import { AsyncTempSwitchExperinceModal as TempSwitchExperienceModal } from './temp-switch-experience-modal';
import type { CreateModalProps } from './types';
import {
	getFirstFieldIdWithError,
	getTabIndexWithError,
	getWatchersFlagConfigFromResponse,
	publishCreatedIssues,
} from './utils';

const CreateModal = ({
	onTransformCreateFormProps,
	setShouldRestore,
	onResetApolloCache,
}: CreateModalProps) => {
	const sessionIdRef = useRef<string>(uuid.v4());

	const [
		{
			payload: triggerIssueCreatePayload,
			displayOnlyRequiredFields,
			disableCreationSuccessFlag,
			isSubTaskCreationOpen,
			isCreateLinkedIssue = false,
			isModalLoadInContext,
			triggerPointKey,
			interactedWithModalChangerButtons,
			inProgressIssueID,
			sessionId,
			wasStateRestored,
			isCustomBoardWithIcc,
			jqlSwimlaneId,
			swimlaneMode,
		},
		{ setSessionId, openIssueCreateModal, resetWasModalRetriggered },
	] = useTriggerIssueCreateModal();

	useEffect(() => {
		if (!sessionId) {
			setSessionId(sessionIdRef.current);
		}
	}, [sessionId, setSessionId]);

	const { showEnforcementIfPendingToShow } = useStorageEnforcement();

	const [
		{
			isLoading: isModalLoading,
			fieldConfig: modalFieldConfig,
			fieldTabs: modalFieldTabs,
			contextFields: modalContextFields,
			meta: modalFieldsDataMeta,
			error: modalFieldsDataError,
			apiLatency: modalApiLatency,
			additionalServices: modalAdditionalServices,
		},
		fetchModalConfig,
	] = useModalLoadQuery({
		displayOnlyRequiredFields,
		triggerPointKey,
		isSubTaskCreationOpen,
		triggerIssueCreatePayload,
		isModalLoadInContext,
	});

	const classificationFieldId = Object.keys(modalFieldConfig).find(
		(key) => modalFieldConfig[key].gqlType === 'JiraDataClassificationField',
	);

	const classificationFieldConfig = classificationFieldId
		? modalFieldConfig[classificationFieldId]
		: undefined;

	const proformaConfiguration = modalAdditionalServices?.proformaConfiguration;
	const { publish } = useGICLifeCycleEvents();

	const dirtyFieldsCurrentSession = useRef<DirtyFields>({});
	const getFormState = useRef<() => FormState | null>(() => null);

	const { data: projectContext } = useProjectContext();

	const [
		{
			isSubmitting,
			isOperationInProgress,
			shouldShowConfigureFieldModal,
			shouldShowFindFieldModal,
			selectedProject,
			selectedIssueType,
			meta,
			shouldShowTempSwitchModal,
			dataFetchError,
			isFormDirty: isCreateFormDirty,
			canCreateAnotherIssue,
			createdIssues,
		},
		{
			resetFormContext,
			toggleConfigureFieldsModalVisibility,
			toggleFindFieldsModalVisibility,
			closeTempSwitchModal,
			setAreConfiguredFieldsModified,
			setForgeModules,
			setCurrentActiveTabIndex,
			setCanCreateAnotherIssue,
			addCreatedIssue,
		},
	] = useFormContext();
	const [, { resetState }] = useInlineFieldsActions();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isGICPositioningEnabled = useIsGICPositioningEnabled();

	const { withMachineLearning } = ff('gic-smart-fields-jira-labs_8uwvr')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useSmartFieldsMachineLearning()
		: { withMachineLearning: false };

	const shouldDisableActions = dataFetchError !== null;

	useEffect(() => {
		if (shouldDisableActions && setShouldRestore) {
			if (selectedProject === null) {
				const analyticsEvent = createAnalyticsEvent({
					actionSubject: 'noselectedproject',
					action: 'error',
				});
				fireUIAnalytics(analyticsEvent, 'createIssueViewError');
			}
			setShouldRestore(false);
		}
	}, [shouldDisableActions, setShouldRestore, selectedProject, createAnalyticsEvent]);

	const initFetchConfig = useCallback(() => {
		// ideally the change should be for all queries, but limitting the scope of change for now. The variable is defaulted to "STANDARD" in the BE.
		const issueCreateType =
			ff('create-linked-issues-trigger-point-issue-view') &&
			isCreateLinkedIssue &&
			isModalLoadInContext
				? {
						issueCreateType: isCreateLinkedIssue
							? ISSUE_CREATE_TYPE.linkedIssue
							: ISSUE_CREATE_TYPE.standard,
					}
				: {};

		if (!triggerIssueCreatePayload) {
			fetchModalConfig({
				variables: getQueryVariables(getIssueCreateProjectForMount(projectContext), null),
			});
		} else {
			const { customFieldsToPreserveOnFormRecreation = [], ...mainPayload } =
				triggerIssueCreatePayload;
			const parentId = mainPayload.parentId || null;
			const additionalContext = mainPayload.additionalContext || null;

			const projectPayload = isEmpty(mainPayload.project) ? null : mainPayload.project;

			fetchModalConfig({
				variables: {
					project: projectPayload,
					issueType: mainPayload.issueType || null,
					requestType: mainPayload.requestType || null,
					defaultValues: mainPayload.defaultValues || null,
					issueTypes: mainPayload.issueTypes || null,
					requestTypes: mainPayload.requestTypes || null,
					parentId,
					additionalContext,
					...issueCreateType,
				},
			});

			dirtyFieldsCurrentSession.current = {
				...dirtyFieldsCurrentSession.current,
				...Object.fromEntries(
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					customFieldsToPreserveOnFormRecreation.map((k: any) => [k, true]),
				),
			};
		}
	}, [
		isCreateLinkedIssue,
		isModalLoadInContext,
		triggerIssueCreatePayload,
		fetchModalConfig,
		projectContext,
	]);

	const [{ siteDomain, selectedCloudId }] = useSitePickerContext();

	const [
		{ isLoading: isTransitionsLoading, error: transitionsError, transitions },
		fetchTransitions,
	] = useFetchTransitionsService();

	const shouldShowStatus = useMemo(
		() => shouldShowStatusField(selectedProject?.projectType, modalFieldConfig),
		[selectedProject, modalFieldConfig],
	);
	const { resetAndReloadCreateForm } = useIssueCreateFromAnywhereContext();

	useEffect(() => {
		if (shouldShowStatus && selectedProject && selectedIssueType && !resetAndReloadCreateForm) {
			fetchTransitions({
				projectId: selectedProject.value,
				issueTypeId:
					selectedIssueType.type === REQUEST_TYPE
						? selectedIssueType.issueTypeId
						: selectedIssueType.value,
				selectedCloudId,
			});
		}
	}, [
		fetchTransitions,
		selectedProject,
		selectedIssueType,
		shouldShowStatus,
		selectedCloudId,
		resetAndReloadCreateForm,
	]);

	const extraContext = useMemo(
		() => ({
			issueType: selectedIssueType?.label,
			projectKey: selectedProject?.projectKey,
			classificationTags: selectedProject?.classificationTags,
		}),
		[selectedProject, selectedIssueType],
	);

	const { customFields, issueAdjustmentModules, filteredOutModules, loadingStatus } =
		useFetchForgeModules(extraContext);

	const hasForgeFinishedLoading = loadingStatus === 'success' || loadingStatus === 'error';

	useEffect(() => {
		if (selectedProject && selectedIssueType) {
			setForgeModules(
				customFields,
				issueAdjustmentModules,
				filteredOutModules,
				hasForgeFinishedLoading,
			);
		}
	}, [
		issueAdjustmentModules,
		setForgeModules,
		customFields,
		hasForgeFinishedLoading,
		filteredOutModules,
		selectedProject,
		selectedIssueType,
		shouldShowStatus,
	]);
	const [showIssuesCreated, setShowIssuesCreated] = useState(false);

	const isApiCallTriggered = useRef(false);
	if (!isApiCallTriggered.current) {
		initFetchConfig();
		inProgressIssueID &&
			publish(GIC_LIFECYCLE_EVENTS.MODAL_OPENED, {
				inProgressIssueID,
			});

		const restoredCreatedIssuesExists = createdIssues?.length > 0;
		restoredCreatedIssuesExists && setShowIssuesCreated(restoredCreatedIssuesExists);

		isApiCallTriggered.current = true;
	}

	const [
		{
			globalEventNamespaces,
			fieldsToAddInPublishedData,
			alertsToBeLinked,
			onIssueCreate,
			onClose,
			displayCreateAnotherIssueToggle,
			callbackPayload,
		},
		{ closeIssueCreateModal, setCallbackPayload },
	] = useTriggerIssueCreateModal();

	const closeIssueCreateModalNew = useCallback(
		(issuesCreated: CreatedIssues) => {
			closeIssueCreateModal(Boolean(issuesCreated?.length));
			inProgressIssueID &&
				publish(GIC_LIFECYCLE_EVENTS.MODAL_CLOSED, {
					callbackPayload,
					inProgressIssueID,
					createdIssues: issuesCreated,
				});
		},
		[callbackPayload, closeIssueCreateModal, inProgressIssueID, publish],
	);

	if (isGICPositioningEnabled()) {
		// combine hook with above when cleaning up gic-refetch-on-discard_cxard
		const [{ shouldInitFetchConfig }, { resetShouldInitFetchConfig }] =
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useTriggerIssueCreateModal();
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			if (shouldInitFetchConfig || resetAndReloadCreateForm) {
				resetShouldInitFetchConfig();
				initFetchConfig();
			}
		}, [
			initFetchConfig,
			resetShouldInitFetchConfig,
			shouldInitFetchConfig,
			resetAndReloadCreateForm,
		]);
	}

	useEffect(() => {
		if (resetAndReloadCreateForm) {
			resetFormContext();
		}
	}, [resetAndReloadCreateForm, resetFormContext]);

	const [shouldShowConfirmDismissModal, setShouldShowConfirmDismissModal] = useState(false);
	const sourceThatFiredModalDismiss = useRef<unknown>(null);

	const [objectId, setObjectId] = useState<string | undefined>(undefined);

	const route = useCurrentRoute();
	const isCompanyManagedBoard =
		route?.name === ROUTE_NAMES.RAPIDBOARD_BOARD ||
		route?.name === ROUTE_NAMES.RAPIDBOARD_USER_BOARD ||
		(ff('jsm-fix-multiple-success-flag-when-gic-on-boards') &&
			route?.name === ROUTE_NAMES.SERVICEDESK_BOARDS);
	const { showFlag } = useFlagsService();

	const showSuccessFlag = useCallback(
		(issueList: CreatedIssues, successFlagParams?: SuccessFlagParams) => {
			if (issueList?.length && !disableCreationSuccessFlag) {
				const flagId = toFlagId(uuid.v4());
				const issueDiscoveryUrl = getIssueDiscoveryUrl(issueList);
				showFlag({
					id: flagId,
					render: (flagProps: FlagCustomRenderProps) => {
						const successFlagProps = {
							key: flagProps.key,
							flagKey: flagProps.key,
							id: flagId,
							issueList,
							issueDiscoveryUrl,
						};
						return <SuccessFlag {...successFlagProps} successFlagParams={successFlagParams} />;
					},
				});

				const analyticsEvent = createAnalyticsEvent({
					actionSubject: 'flag',
					action: 'viewed',
				});
				fireUIAnalytics(analyticsEvent, 'createIssueSuccessNotification');
			}
		},
		[createAnalyticsEvent, disableCreationSuccessFlag, showFlag],
	);

	useEffect(() => {
		const api = {
			// @ts-expect-error - TS7006 - Parameter 'params' implicitly has an 'any' type.
			renderSuccessFlag: (params) => {
				// @ts-expect-error - TS7006 - Parameter 'issue' implicitly has an 'any' type.
				const issueList = params.issues.map((issue) => ({
					issueId: issue.issueId,
					issueKey: issue.issueKey,
				}));
				successFlagDisplay.stop();
				showSuccessFlag(issueList, params);
			},
		};

		if (isCompanyManagedBoard) {
			// eslint-disable-next-line jira/wrm/no-load-bridge
			loadBridge({ name: 'jira-agile/rapid/ui/gic-success-flag/bridge' }).then((bridge) =>
				// @ts-expect-error - TS2571 - Object is of type 'unknown'.
				bridge(api),
			);
		}
	}, [showSuccessFlag, isCompanyManagedBoard]);

	// combine this with above when cleaning up gic.positioning
	const [{ minimizableModalView }] = isGICPositioningEnabled()
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useTriggerIssueCreateModal()
		: [{ minimizableModalView: undefined }];

	const [{ shouldPushMiniModalDiscoverability }] = useTriggerIssueCreateModal();

	const { updateView } = usePersistentIssueCreateModalViewResource();

	const performCleanUp = useCallback(
		(shouldPublishCreatedIssues = false, issuesCreated?: CreatedIssues) => {
			const issueList: CreatedIssues = issuesCreated ?? createdIssues ?? [];

			if (!isCompanyManagedBoard) {
				showSuccessFlag(issueList);
			} else {
				successFlagDisplay.start();
			}

			closeIssueCreateModalNew(issueList);
			if (issuesCreated) {
				updateView(minimizableModalView === 'mini' ? 'mini' : 'modal');
			}

			if (shouldPublishCreatedIssues) {
				publishCreatedIssues({
					createdIssues: issueList,
					globalEventNamespaces,
					fieldsToAddInPublishedData,
					alertsToBeLinked,
					isSessionComplete: true,
					onIssueCreate: null,
					onClose,
					triggerPointKey,
					publishGICLifecycle: publish,
					inProgressIssueID,
					callbackPayload,
					selectedCloudId,
					jqlSwimlaneId,
				});
			}
		},
		[
			createdIssues,
			isCompanyManagedBoard,
			showSuccessFlag,
			closeIssueCreateModalNew,
			globalEventNamespaces,
			fieldsToAddInPublishedData,
			alertsToBeLinked,
			onClose,
			triggerPointKey,
			publish,
			inProgressIssueID,
			callbackPayload,
			selectedCloudId,
			minimizableModalView,
			updateView,
			jqlSwimlaneId,
		],
	);

	useEffect(() => {
		if (ff('jira-intelligent-issue-breakdown_18syl') && minimizableModalView === 'ai-enabled') {
			setCallbackPayload({ wasFormDirty: isCreateFormDirty });
		}
	}, [isCreateFormDirty, setCallbackPayload, minimizableModalView]);

	const onCreateAnotherChange = (_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent): void => {
		setCanCreateAnotherIssue(!canCreateAnotherIssue);

		fireUIAnalytics(analyticsEvent, 'createAnotherIssue', {
			projectType: selectedProject ? transformProjectType(selectedProject.projectType) : 'none',
		});
	};

	const handleTabIndexEmptyContainingRequiredField = () => {
		try {
			const fieldValues = getFormState.current()?.values || null;

			const tabIndex: number = getTabIndexWithError(modalFieldTabs, fieldValues, modalFieldConfig);

			setCurrentActiveTabIndex(tabIndex);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: {
					packageName: 'jiraIssueCreate',
					id: 'gicModalTabChangeOnSubmit',
					teamName: 'gryffindor',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	};

	const handleEmptyAndRequiredFields = () => {
		try {
			const fieldValues: FormFieldValues | null = getFormState.current()?.values || null;

			let emptyRequiredFields: string[] = [];
			if (modalFieldConfig) {
				emptyRequiredFields = Object.keys(modalFieldConfig)?.filter(
					(fieldId: string) =>
						Boolean(modalFieldConfig[fieldId].isRequired) &&
						fieldValues &&
						isEmpty(fieldValues[fieldId]),
				);
			}

			const firstFieldIdWithError = getFirstFieldIdWithError({
				fields: Object.keys(fieldValues || {}),
				emptyRequiredFields,
			});

			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			scrollToField(firstFieldIdWithError as string);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: {
					packageName: 'jiraIssueCreate',
					id: 'gicModalNextRequiredFieldFocusOnSubmit',
					teamName: 'gryffindor',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	};

	const { atlassianAccountId } = useTenantContext();

	const onSubmit = (_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent): void => {
		handleTabIndexEmptyContainingRequiredField();
		handleEmptyAndRequiredFields();

		fireUIAnalytics(analyticsEvent, 'createIssue', {
			projectType: selectedProject ? transformProjectType(selectedProject.projectType) : 'none',
			gicSessionId: sessionIdRef.current,
		});
	};

	const showWatchersWarningOrErrorFlag = useCallback(
		(response?: IssueCreateResponse['watchers']) => {
			const config = getWatchersFlagConfigFromResponse(response);
			if (config) {
				/**
				 * Showing the watchers warning/error flag slightly after the issue-create-success-flag
				 * so they visually appear one after another in order and the user would be able to
				 * actually see the success flag sliding in first and then being pushed down.
				 */
				setTimeout(() => {
					showFlag(config);
				}, 600);
			}
		},
		[showFlag],
	);

	const onIssueCreateSuccess = useCallback(
		(createdIssue: CreatedIssue, watchersResponse?: IssueCreateResponse['watchers']): void => {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const issuesCreated = addCreatedIssue(createdIssue)!;

			setObjectId(String(createdIssue?.issueId));
			setShowIssuesCreated(showIssuesCreated || canCreateAnotherIssue);

			publishCreatedIssues({
				createdIssues: [createdIssue],
				globalEventNamespaces,
				fieldsToAddInPublishedData,
				alertsToBeLinked,
				onIssueCreate,
				siteDomain,
				selectedCloudId,
				onClose,
				inProgressIssueID,
				publishGICLifecycle: publish,
				isIssueCreateSuccess: true,
				callbackPayload,
				jqlSwimlaneId,
			});

			if (!canCreateAnotherIssue) {
				performCleanUp(true, issuesCreated);
			}

			showWatchersWarningOrErrorFlag(watchersResponse);
		},
		[
			showIssuesCreated,
			canCreateAnotherIssue,
			globalEventNamespaces,
			fieldsToAddInPublishedData,
			alertsToBeLinked,
			onIssueCreate,
			siteDomain,
			selectedCloudId,
			onClose,
			inProgressIssueID,
			publish,
			callbackPayload,
			addCreatedIssue,
			performCleanUp,
			showWatchersWarningOrErrorFlag,
			jqlSwimlaneId,
		],
	);

	const [dismissModalType, setDismissModalType] = useConfirmDismissAiSuggestedModal();
	const [
		{ shouldShowConfirmDismissFromGlobalCreate, shouldShowConfirmDismissFromCurrentlyEditedIssue },
		{ handleNextModalState },
	] = useShouldShowConfirmDismissAiSuggestedModal();

	const showConfirmDismissModal = (source: string): void => {
		sourceThatFiredModalDismiss.current = source;
		setShouldShowConfirmDismissModal(true);
	};

	/**
	 * If a user opens the Global Issue Create from top navigation,
	 * while the minimizable AI modal is open, or they
	 * navigate away while current issue has been modified,
	 * we trigger a warning modal.
	 */
	useEffect(() => {
		if (!ff('jira-intelligent-issue-breakdown_18syl')) {
			return;
		}
		if (
			shouldShowConfirmDismissFromGlobalCreate ||
			(shouldShowConfirmDismissFromCurrentlyEditedIssue && shouldShowConfirmDismissFromGlobalCreate)
		) {
			handleNextModalState();
			setDismissModalType(DISCARD_MODAL_TYPE.DISCARD_ALL_FROM_GLOBAL_CREATE);
			return;
		}
		if (shouldShowConfirmDismissFromCurrentlyEditedIssue) {
			handleNextModalState();
			setDismissModalType(DISCARD_MODAL_TYPE.DISCARD_CURRENT_EDITED);
		}
	}, [
		shouldShowConfirmDismissFromGlobalCreate,
		shouldShowConfirmDismissFromCurrentlyEditedIssue,
		handleNextModalState,
		setDismissModalType,
	]);

	const onAiSuggestedModalDiscard = (
		_: SyntheticEvent,
		analyticsEvent: UIAnalyticsEvent,
		type: ConfirmDismissAiSuggestedModal,
	) => {
		switch (type) {
			case DISCARD_MODAL_TYPE.DISCARD_ALL_FROM_GLOBAL_CREATE:
				publish(GIC_LIFECYCLE_EVENTS.MODAL_DISCARD_ALL_SUGGESTED_ISSUES, {});
				resetWasModalRetriggered(true);
				// Recreate global-create-button trigger
				openIssueCreateModal({
					disableCreationSuccessFlag: false,
					triggerPointKey: 'global-create-button',
				});
				break;
			case DISCARD_MODAL_TYPE.DISCARD_CURRENT_SUGGESTED:
				closeIssueCreateModal(false);
				onConfirmDismissModalConfirm(_, analyticsEvent);
				break;
			case DISCARD_MODAL_TYPE.DISCARD_CURRENT_EDITED:
				closeIssueCreateModal(false);
				publish(GIC_LIFECYCLE_EVENTS.MODAL_DISCARD_SUGGESTED_ISSUE_CHANGES, {});
				break;
			default:
		}
		setDismissModalType(undefined);
	};

	const onAiSuggestedModalClose = () => {
		setDismissModalType(undefined);
	};

	const hideConfirmDismissModal = (): void => {
		setShouldShowConfirmDismissModal(false);
		sourceThatFiredModalDismiss.current = null;
	};

	const onModalClose = (
		_: SyntheticEvent,
		analyticsEvent: UIAnalyticsEvent,
		source: string,
	): void => {
		fireUIAnalytics(analyticsEvent, source, {
			projectType: selectedProject ? transformProjectType(selectedProject.projectType) : 'none',
		});
		showEnforcementIfPendingToShow();
		performCleanUp(true);
		resetState();
	};

	const onModalCloseWithConfirm =
		(source: string) =>
		(_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent): void => {
			if (ff('jira-intelligent-issue-breakdown_18syl') && minimizableModalView === 'ai-enabled') {
				setDismissModalType(DISCARD_MODAL_TYPE.DISCARD_CURRENT_SUGGESTED);
				return;
			}

			if (!isCreateFormDirty) {
				onModalClose(_, analyticsEvent, source);
				return;
			}
			showConfirmDismissModal(source);
		};

	const onConfirmDismissModalCancel = () => {
		hideConfirmDismissModal();
	};

	const onConfirmDismissModalConfirm = (_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		const source = (sourceThatFiredModalDismiss && sourceThatFiredModalDismiss.current) || '';
		// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string'.
		onModalClose(_, analyticsEvent, source);
		hideConfirmDismissModal();

		if (ff('jira-intelligent-issue-breakdown_18syl') && minimizableModalView === 'ai-enabled') {
			setTimeout(() => {
				publish(GIC_LIFECYCLE_EVENTS.MODAL_DISCARD_SUGGESTED_ISSUE, {});
			});
		}
	};

	const [resetFormFieldsCounter, setResetFormFieldsCounter] = useState(0);
	const incrementResetCounter = useCallback(() => {
		setResetFormFieldsCounter((n) => n + 1);
	}, []);

	const hideConfigureFieldsModal = (): void => {
		toggleConfigureFieldsModalVisibility(false);
	};

	const hideFindFieldModal = (): void => {
		toggleFindFieldsModalVisibility(false);
	};

	const hideTempSwitchModal = (shouldCloseParentIssueCreateModal: boolean): void => {
		closeTempSwitchModal();
		if (shouldCloseParentIssueCreateModal) {
			performCleanUp();
		}
	};

	// @ts-expect-error - TS2525 - Initializer provides no value for this binding element and the binding element has no default value. | TS7031 - Binding element 'shouldShowChangerSpotlight' implicitly has an 'any' type. | TS2525 - Initializer provides no value for this binding element and the binding element has no default value. | TS7031 - Binding element 'onOpenSpotlight' implicitly has an 'any' type. | TS2525 - Initializer provides no value for this binding element and the binding element has no default value. | TS7031 - Binding element 'onCloseSpotlight' implicitly has an 'any' type.
	const { shouldShowChangerSpotlight, onOpenSpotlight, onCloseSpotlight } =
		isGICPositioningEnabled()
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useChangerSpotlight(minimizableModalView)
			: {};

	// resetting form context should happen after the component is unmounted (if done before, certain components/fields were throwing error)
	// @ts-expect-error - TS2322 - Type '() => void | Promise<void>' is not assignable to type 'void | Destructor'.
	useEffect(() => resetFormContext, [resetFormContext]);

	// add sprint field to published data to enable dynamic success flags for scrum projects
	useEffect(() => {
		const sprintFieldId = Object.keys(modalFieldConfig).find(
			(key) => modalFieldConfig[key].gqlType === 'JiraSprintField',
		);
		if (sprintFieldId !== undefined) {
			fieldsToAddInPublishedData.push(sprintFieldId);
		}
	}, [fieldsToAddInPublishedData, modalFieldConfig]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const fieldPropsExtensions = isGICPositioningEnabled()
		? {
				description: {
					onFocus: onOpenSpotlight,
					onBlur: onCloseSpotlight,
				},
			}
		: undefined;

	const preselectedStatus = triggerIssueCreatePayload?.status?.statusId;

	const classificationFieldProps: ClassificationFieldProps =
		useClassificationFieldProps(classificationFieldConfig);

	const buildCreateFormProps = useCallback(
		(): CreateFormProps => ({
			isLoading: isModalLoading,
			fieldConfig: modalFieldConfig,
			fieldTabs: modalFieldTabs,
			contextFields: modalContextFields,
			meta: modalFieldsDataMeta,
			error: modalFieldsDataError,
			apiLatency: modalApiLatency,
			onFetchConfig: fetchModalConfig,
			onIssueCreateSuccess,
			canCreateAnotherIssue,
			dirtyFieldsCurrentSession,
			getFormState,
			onFormFieldsReset: incrementResetCounter,
			fieldPropsExtensions,
			onResetApolloCache,
			filteredOutModules,
			...(ff('support-data-classification-field-in-gic_x6lda') &&
			classificationFieldConfig &&
			Array.isArray(classificationFieldConfig?.allowedValues) &&
			classificationFieldConfig.allowedValues.length > 0
				? {
						classificationFieldProps,
					}
				: {}),
			...(shouldShowStatus
				? {
						statusFieldProps: {
							preselectedTransition: transitions?.find(
								(transition: StatusTransition) => transition.to.id === preselectedStatus,
							),
							initialTransition: transitions?.find((it: StatusTransition) => it.isInitial),
							availableTransitions: transitions,
							isTransitionsLoading,
							transitionsError,
						},
					}
				: Object.freeze({})),
			proformaConfiguration,
			watchersConfigurations: modalAdditionalServices.watchersConfigurations,
		}),
		[
			isModalLoading,
			modalFieldConfig,
			modalFieldTabs,
			modalContextFields,
			modalFieldsDataMeta,
			modalFieldsDataError,
			modalApiLatency,
			fetchModalConfig,
			onIssueCreateSuccess,
			canCreateAnotherIssue,
			incrementResetCounter,
			fieldPropsExtensions,
			classificationFieldConfig,
			classificationFieldProps,
			shouldShowStatus,
			transitions,
			isTransitionsLoading,
			transitionsError,
			preselectedStatus,
			proformaConfiguration,
			onResetApolloCache,
			modalAdditionalServices.watchersConfigurations,
			filteredOutModules,
		],
	);

	const createFormProps = useMemo(() => {
		let formProps = buildCreateFormProps();
		if (onTransformCreateFormProps) {
			formProps = onTransformCreateFormProps(formProps);
		}
		return formProps;
	}, [buildCreateFormProps, onTransformCreateFormProps]);

	const renderFormOrError = () => {
		if (shouldDisableActions) {
			return <ErrorState />;
		}

		return <CreateForm {...createFormProps} />;
	};

	const currentUIModificationsConsentState = useUIModificationsConsentStatus();

	const getShouldDisableSubmit = (): boolean =>
		currentUIModificationsConsentState === UIM_CONSENT_REQUIRED ||
		!meta.areRequiredFieldsSupported ||
		shouldDisableActions ||
		!isEmpty(meta.queryErrorMessage);

	// initialise the request type fields layout preference on modal load
	// internally, this will not re-fetch if previously done
	const [{ data: isRequestTypeFieldsLayoutEnabled }, { initialise }] =
		useRequestTypeFieldsLayoutPreference();
	useEffect(() => {
		if (atlassianAccountId) {
			initialise(atlassianAccountId, createAnalyticsEvent);
		}
	}, [atlassianAccountId, createAnalyticsEvent, initialise]);

	const isRequestTypeSelected = selectedIssueType?.type === REQUEST_TYPE;

	const renderConfigureFieldsModal = () =>
		shouldShowConfigureFieldModal && selectedProject && selectedIssueType ? (
			<ConfigureFieldsModal
				onCancel={hideConfigureFieldsModal}
				project={{
					projectId: selectedProject.value,
					projectType: selectedProject.projectType,
				}}
				issueTypeId={
					isRequestTypeSelected ? selectedIssueType.issueTypeId : selectedIssueType.value
				}
				requestTypeKey={isRequestTypeSelected ? selectedIssueType.requestTypeKey : undefined}
				parentId={triggerIssueCreatePayload?.parentId?.parentId}
				setAreConfiguredFieldsModified={setAreConfiguredFieldsModified}
				onlyDisplayAllFieldsOption={isRequestTypeFieldsLayoutEnabled && isRequestTypeSelected}
				messageContent={isRequestTypeSelected ? <JsmRequestTypeFieldsChangeboarding /> : undefined}
			/>
		) : null;

	const renderFindFieldModal = () =>
		shouldShowFindFieldModal && selectedProject && selectedIssueType ? (
			<FindFieldModal
				project={{
					projectId: selectedProject.value,
					nextGenProject: selectedProject.isSimplified,
					projectType: selectedProject.projectType,
				}}
				issueTypeId={
					selectedIssueType.type === REQUEST_TYPE
						? selectedIssueType.issueTypeId
						: selectedIssueType.value
				}
				operation="CREATE"
				onCancel={hideFindFieldModal}
			/>
		) : null;

	const renderTempSwitchExperienceModal = () =>
		shouldShowTempSwitchModal && selectedProject ? (
			<TempSwitchExperienceModal
				onClose={hideTempSwitchModal}
				projectType={selectedProject.projectType}
			/>
		) : null;

	const renderConfirmDismissModalAiSuggested = () =>
		ff('jira-intelligent-issue-breakdown_18syl') &&
		selectedProject && (
			<DiscardSuggestionsModal
				onDiscard={onAiSuggestedModalDiscard}
				onClose={onAiSuggestedModalClose}
				modalType={dismissModalType}
			/>
		);

	const renderConfirmDismissModal = () =>
		shouldShowConfirmDismissModal && selectedProject ? (
			<ConfirmDismissModal
				project={{
					projectId: selectedProject.value,
					projectType: selectedProject.projectType,
				}}
				sessionId={sessionIdRef.current}
				onCancel={onConfirmDismissModalCancel}
				onConfirm={onConfirmDismissModalConfirm}
			/>
		) : null;

	const renderExistingGicWarningModal = () => <ExistingGICWarning />;

	const renderDisabledRestoreNavigationPrompt = () => (
		<NavigateWithoutRestorePrompt setShouldRestore={setShouldRestore} />
	);

	const smartCardProviderFF = useMemo(
		() => ({
			enableFlexibleBlockCard: ff('issue.details.editor.enable-flexible-block-card'),
		}),
		[],
	);

	const modalWrapper = (
		<ModalWrapper
			onModalCloseWithConfirm={onModalCloseWithConfirm}
			shouldDisableActions={shouldDisableActions}
			onSubmit={onSubmit}
			isSubmitting={isSubmitting}
			isOperationInProgress={isOperationInProgress}
			canCreateAnotherIssue={canCreateAnotherIssue}
			onCreateAnotherChange={onCreateAnotherChange}
			getShouldDisableSubmit={getShouldDisableSubmit}
			createdIssues={createdIssues}
			displayCreateAnotherIssueToggle={displayCreateAnotherIssueToggle}
			shouldShowChangerSpotlight={
				isGICPositioningEnabled()
					? !interactedWithModalChangerButtons && shouldShowChangerSpotlight
					: undefined
			}
			showIssuesCreated={showIssuesCreated}
			initialSummaryValue={
				isEmpty(createFormProps.fieldConfig)
					? null
					: // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						(createFormProps.fieldConfig?.summary?.defaultValue as string) || ''
			}
			projectType={selectedProject?.projectType}
			selectedIssueType={selectedIssueType}
			setShouldRestore={setShouldRestore}
			watchersConfigurations={modalAdditionalServices.watchersConfigurations}
		>
			{!isGICPositioningEnabled() && <MountEvent onMount={fireScreenAnalyticsDeferred} />}
			<SmartCardProvider featureFlags={smartCardProviderFF}>
				{renderFormOrError()}
			</SmartCardProvider>
			<UiModificationsEntryPoint
				issueTypeFieldType={modalContextFields.issuetype?.type || ''}
				modalFieldConfig={modalFieldConfig}
				modules={issueAdjustmentModules}
				resetFormFieldsCounter={resetFormFieldsCounter}
				selectedIssueType={selectedIssueType}
				selectedProject={selectedProject}
				triggerPointKey={triggerPointKey}
			/>
			{renderConfigureFieldsModal()}
			{renderFindFieldModal()}
			{renderTempSwitchExperienceModal()}
			{renderConfirmDismissModal()}
			{isGICPositioningEnabled() && renderExistingGicWarningModal()}
			{renderDisabledRestoreNavigationPrompt()}
			{renderConfirmDismissModalAiSuggested()}
		</ModalWrapper>
	);

	return (
		<ContextualAnalyticsData
			sourceType={MODAL}
			sourceName="createIssue"
			containerType="project"
			objectType="issue"
			objectId={objectId}
			containerId={selectedProject ? selectedProject.value : ''}
			attributes={{
				isNewGIC: true,
				isCreatingAnotherIssue: canCreateAnotherIssue,
				isSubtask: isSubTaskCreationOpen,
				triggerPointKey,
				...(selectedProject ? { nextGenProject: selectedProject.isSimplified } : {}),
				...(isGICPositioningEnabled() ? { gicViewState: minimizableModalView } : {}),
				...(ff('create-linked-issues-trigger-point-issue-view') ? { isCreateLinkedIssue } : {}),
				hasProformaForm: !!proformaConfiguration?.hasProformaForm,
				wasStateRestored,
				isDiscoverabilityPush:
					!!shouldPushMiniModalDiscoverability && isPartOfDiscoverabilityTriggers(triggerPointKey),
				smartFieldsEnabled: withMachineLearning,
				sessionId: !sessionId ? sessionIdRef.current : sessionId,
				isCustomBoardWithIcc,
				...(ff('icc-for-query-swimlanes') ? { swimlaneMode } : {}),
			}}
		>
			<UiModificationsContainer>{modalWrapper}</UiModificationsContainer>
		</ContextualAnalyticsData>
	);
};

export default CreateModal;
